import React from 'react';
import Icon from './Icon';

/* Building blocks shared by the admin detail pages (legal services request,
   service order, rental application). */

export const fmtDate = (v?: string | null): string => {
  if (!v) return '—';
  /*
   * DATEONLY values ('2026-05-20') parse as UTC midnight with Date(), which
   * renders as the previous day anywhere west of Greenwich — build them as a
   * local date instead.
   */
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const fmtDateTime = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const cap = (v?: string | null): string =>
  v ? v.charAt(0).toUpperCase() + v.slice(1) : '—';

export const DetailCard: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div className="card p-6">
    <div className="flex items-center gap-2.5 mb-3">
      <Icon name={icon} size={18} color="var(--brand)" />
      <span className="text-base font-bold">{title}</span>
    </div>
    {children}
  </div>
);

export const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-4 py-2.5 border-b border-g100 last:border-b-0">
    <span className="text-[13px] text-g500 flex-shrink-0">{label}</span>
    <span className="text-[13px] text-ink font-semibold text-right min-w-0 break-words">{children}</span>
  </div>
);

/** Square image thumbnail that opens the image viewer, with a hover hint. */
export const ImageThumb: React.FC<{ src: string; alt: string; onOpen: () => void; size?: number }> = ({
  src,
  alt,
  onOpen,
  size = 128,
}) => (
  <button
    type="button"
    onClick={onOpen}
    title="View larger"
    className="group relative rounded-xl overflow-hidden border border-g200 flex-shrink-0"
    style={{ width: size, height: size, background: 'var(--g100)', cursor: 'zoom-in' }}
  >
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
    />
    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/35 transition-colors">
      <span className="opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
        <Icon name="maximize" size={16} color="var(--ink)" />
      </span>
    </span>
  </button>
);
